import { useNavigate } from 'react-router-dom';
import { Building2, ArrowRight, LogOut } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { PLANS } from '../lib/plans';
import { LogoFull } from '../components/Logo';
import { toast } from 'sonner';

const ROLE_LABELS = {
  admin: 'Administrador (DPO)',
  compliance_officer: 'Compliance Officer',
  area_operativa: 'Área operativa',
  auditor_externo: 'Auditor Externo',
};

export default function SelectOrg() {
  const { memberships, selectOrg, logout, organization, markSelectionShown } = useAuth();
  const nav = useNavigate();

  const pick = async (m) => {
    try {
      await selectOrg(m.organization_id);
      markSelectionShown();
      toast.success(`Ingresando como ${m.razon_social}`);
      nav('/app/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pudimos cambiar de organización');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex flex-col" data-testid="select-org-page">
      <header className="border-b border-[#E5E7EB] bg-white">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <LogoFull height={26} />
          <button
            onClick={logout}
            className="text-[13px] text-[#6B7280] hover:text-[#1C2329] flex items-center gap-1.5"
            data-testid="select-org-logout"
          >
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          <div className="pv-eyebrow mb-3">Elegir organización</div>
          <h1 className="text-[32px] md:text-[40px] font-semibold tracking-tight text-[#1C2329] leading-tight">
            Con qué empresa quiere trabajar hoy.
          </h1>
          <p className="mt-3 text-[15px] text-[#6B7280] max-w-xl">
            Su usuario tiene acceso a {memberships.length} organizaciones. Puede cambiar entre ellas en cualquier momento desde el menú lateral.
          </p>

          <div className="mt-8 space-y-2" data-testid="select-org-list">
            {memberships.map((m) => {
              const plan = PLANS[m.plan || 'pyme'];
              const isCurrent = m.organization_id === organization?.id;
              return (
                <button
                  key={m.organization_id}
                  onClick={() => pick(m)}
                  className={`w-full text-left p-4 md:p-5 border ${
                    isCurrent ? 'border-[#2A9B68] bg-[#F0FBF5]' : 'border-[#E5E7EB] bg-white hover:border-[#143A34]'
                  } transition-colors group flex items-center gap-4`}
                  data-testid={`select-org-option-${m.organization_id}`}
                >
                  <div className="w-11 h-11 border border-[#E5E7EB] bg-[#F7F8FA] flex items-center justify-center shrink-0">
                    {m.logo_url ? (
                      <img src={m.logo_url} alt={m.razon_social} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      <Building2 size={18} className="text-[#6B7280]" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[15.5px] font-semibold text-[#1C2329] truncate">{m.razon_social}</span>
                      {isCurrent && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-[#2A9B68] text-white uppercase tracking-wider font-semibold">Actual</span>
                      )}
                    </div>
                    <div className="text-[12px] text-[#6B7280] mt-0.5">
                      {m.rut} · <span className="text-[#374151]">{ROLE_LABELS[m.role] || m.role}</span> · Plan {plan?.name}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-[#9CA3AF] group-hover:text-[#143A34] shrink-0" />
                </button>
              );
            })}
          </div>

          <div className="mt-6 text-[12px] text-[#6B7280]">
            Todo lo que haga queda registrado en la organización activa. La bitácora es independiente por empresa.
          </div>
        </div>
      </div>
    </div>
  );
}
